import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { TextFieldInput } from '../TextFieldInput';
import { Tag, TagVariants } from '../Tag';
import { Themes } from '../../style/theme-consts';
import { InputHeights } from '../../utils/consts';
import { getFilterOptions, getOptionLabel } from './autocomplete-utils';

import * as S from './styled';

export const ENTER = 'Enter';
const DELAY_MS_DEBOUNCE_SEARCH = 1000;
const STRING_MIN_LENGTH = 3;

const LOCAL_STRINGS = {
  noOptions: 'No options',
  searching: 'Searching...',
  min_search_text_length: 'Enter at least'
};

const AddTagsAutocomplete = ({
  testId,
  width = '100%',
  height = InputHeights.LARGE,
  onChange,
  placeholder,
  disabled,
  error,
  helperText = null,
  value,
  minLength = STRING_MIN_LENGTH,
  delay = DELAY_MS_DEBOUNCE_SEARCH,
  fetchOptionsData,
  excludedChars = [],
  tagRenderer,
  mode,
  freeSolo = true,
  splitOnSpace = true
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef();

  // No fetch function means this is a pure tag-entry input (user types their own
  // values) — there are no remote suggestions to load or surface.
  const hasRemoteSearch = typeof fetchOptionsData === 'function';

  const submitHandler = currentValue => {
    if (!currentValue) return;
    // With splitOnSpace off, the whole input is one tag — lets consumers add multi-word values.
    const currValues = (splitOnSpace ? currentValue.split(' ') : [currentValue.trim()]).filter(Boolean);
    const updatedValue = [...value, ...currValues.filter(item => !value.includes(item))];
    onChange?.(updatedValue);
    setInputValue('');
  };

  const onAddChangeHandler = (_, newValue) => {
    const val = newValue?.inputValue || newValue?.value || newValue;
    if (typeof val === 'string') {
      submitHandler(val);
    }
  };

  const onKeyDownHandler = e => {
    if (e.key === ENTER && inputValue?.trim()) {
      e.preventDefault();
      e.stopPropagation();
      submitHandler(inputValue);
    }
  };

  const removeTagHandler = deletedItem => () => {
    onChange?.(value.filter(tag => tag !== deletedItem));
  };

  // Debounced search function
  const debouncedFetch = useCallback(
    debounce(async val => {
      setLoading(true);
      try {
        const result = await fetchOptionsData(val);
        setOptions(result);
      } catch (err) {
        setOptions([]);
      }
      setLoading(false);
    }, delay),
    [fetchOptionsData]
  );

  const containsExcludedChar = (input, chars) => chars.some(char => input.includes(char));

  useEffect(() => {
    if (!hasRemoteSearch) return;

    if (containsExcludedChar(inputValue, excludedChars)) {
      debouncedFetch.cancel();
      setOptions([]);
      setLoading(false);
      return;
    }

    if (!inputValue || inputValue.length < minLength) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debouncedFetch(inputValue);
  }, [debouncedFetch, inputValue, minLength]);

  const onInputChangeHandler = (_, newInputValue, reason) => {
    if (reason !== 'reset') {
      setInputValue(newInputValue);
    }
  };

  const buildNoOptionText = useMemo(() => {
    if (!hasRemoteSearch) return null;
    if (loading) {
      // API was sent and waiting for a response
      return (
        <S.Searching>
          {LOCAL_STRINGS.searching}
          <S.CircularProgress className="loading" size={15} />
        </S.Searching>
      );
    }
    if (inputValue?.length < minLength) {
      return `${LOCAL_STRINGS.min_search_text_length} ${minLength} characters`;
    }
    if (inputValue?.length >= minLength) {
      return LOCAL_STRINGS.noOptions;
    }
  }, [inputValue?.length, loading, minLength, hasRemoteSearch]);

  const compProps = useMemo(
    () => ({
      paper: { classes: { root: mode }, sx: S.POPPER_STYLE },
      popupIndicator: { sx: { display: 'none' } }
    }),
    [mode]
  );

  return (
    <S.AddTagAutocompleteWrapper>
      <S.Autocomplete
        value={inputValue}
        onChange={onAddChangeHandler}
        onInputChange={onInputChangeHandler}
        filterOptions={getFilterOptions(loading, freeSolo)}
        onKeyDown={onKeyDownHandler}
        {...(hasRemoteSearch ? {} : { open: Boolean(inputValue?.trim()) })}
        selectOnFocus
        clearOnBlur
        disablePortal
        disabled={disabled}
        handleHomeEndKeys
        componentsProps={compProps}
        data-testid={testId}
        $width={width}
        $height={height}
        id="add-tags-autocomplete"
        options={options}
        noOptionsText={buildNoOptionText}
        getOptionLabel={getOptionLabel}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <S.StyledMenuItem key={key} {...optionProps} data-testid={!!testId && `${testId}-option_${option.label}`}>
              <S.StyledOption>{option.label}</S.StyledOption>
            </S.StyledMenuItem>
          );
        }}
        renderInput={params => (
          <TextFieldInput
            {...params}
            disabled={disabled}
            testId={!!testId && `${testId}-input`}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            inputRef={inputRef}
            InputProps={{
              ...params.InputProps
            }}
          />
        )}
      />
      <S.TagWrapper $width={width}>
        {value?.map(item => (
          <Tag
            key={item}
            content={tagRenderer?.(item) || item}
            onClose={disabled ? null : removeTagHandler(item)}
            variant={TagVariants.CHIP}
            testId={!!testId && `${testId}-tag_${item}`}
          />
        ))}
      </S.TagWrapper>
    </S.AddTagAutocompleteWrapper>
  );
};

AddTagsAutocomplete.propTypes = {
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT]),
  testId: PropTypes.string,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.oneOf(['LARGE', 'MEDIUM', 'SMALL', 'TINY', PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  value: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
      dotColor: PropTypes.string,
      children: PropTypes.array
    })
  ),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  loading: PropTypes.bool,
  helperText: PropTypes.string,
  minLength: PropTypes.string,
  delay: PropTypes.string,
  fetchOptionsData: PropTypes.func,
  tagRenderer: PropTypes.func,
  excludedChars: PropTypes.array,
  freeSolo: PropTypes.bool,
  splitOnSpace: PropTypes.bool
};

export default AddTagsAutocomplete;
